import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFeatures } from '@atc/features';

import { esntialEligibilityFetcher } from 'reaxl-ecommerce';

import { inventoryDuck, paymentsDuck } from '@/ducks';

import { vdpResultsDuck } from '@/ducks/vdp';

/**
 * Manages check for Esntial eligibility on page load
 * Works with middlware to reinstate ecomm links if a
 * vehicle is configured for Esntial and is eligible,
 * or removes Esntial object if the vehicle isn't eligible.
 *
 * @returns null
 */
function EsntialEligibilityContainer(props) {
    const {
        esntial_eligibility_v2: [enableV2],
    } = useFeatures(['esntial_eligibility_v2']);

    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const listing = typeof props?.listing !== 'undefined' ? props.listing : vdpListing;

    const dispatch = useDispatch();

    const {
        esntial,
        id,
        vin,
        owner: listingOwner,
        marketExtension: { parentOwnerId: parentId } = {},
        year,
        make,
        model,
        trim: listingTrim,
        mileage: listingMileage,
        listingType,
        pricingDetail = {},
    } = listing;

    const { incentive, msrp, salePrice } = pricingDetail;
    const isCertified = listingType?.toLowerCase() === 'certified';
    const listingPrice = incentive || salePrice || msrp;
    const mileage = typeof listingMileage === 'object' ? listingMileage?.value : listingMileage;
    const ownerId = parentId || (typeof listingOwner === 'object' ? listingOwner?.id : listingOwner);
    const trim = typeof listingTrim === 'object' ? listingTrim?.name : listingTrim;
    const vehicleCondition = listingType?.toLowerCase() === 'certified' ? 'used' : listingType?.toLowerCase();

    const payloadItems = {
        isCertified,
        listingPrice,
        make,
        mileage,
        model,
        msrp,
        ownerId,
        trim,
        vehicleCondition,
        vin,
        year,
    };

    // Uses v1 endpoint if FF is not enabled
    const v1EsntialEndpoint = (esntial?.eligibilityService)?.replace('vehicle/v2/verify', 'search/verify');
    const eligibilityURL = enableV2 ? esntial?.eligibilityService : v1EsntialEndpoint;

    const existingDeal = useSelector((state) => paymentsDuck?.selectors?.getDealByListingVin(state, vin)) || {};
    const disableEsntial = () => dispatch(inventoryDuck.creators.updateInventoryItem(id, {
        esntial: undefined,
        ecommWebsite: undefined,
    }));

    const isV2Enabled = enableV2 && eligibilityURL.includes('/v2/');

    useEffect(() => {
        if (existingDeal?.isEcommerce) {
            disableEsntial();
        } else if (esntial?.ecommWebsite) {
            // Checks for feature flag and if the updated endpoint is used
            esntialEligibilityFetcher(eligibilityURL, (isV2Enabled ? payloadItems : vin)).then((response) => {
                if (response?.totalMatched >= 1 || response?.[0]?.sellable) {
                    // Enable ecommWebsite and dealerPreferredCTA to show
                    // Esntial entry points
                    dispatch(inventoryDuck.creators.updateInventoryItem(id, {
                        ecommWebsite: esntial.ecommWebsite,
                        owner: {
                            ...listing.owner,
                            dealerPreferredCTA: esntial.deemphasized ? undefined : 'WEBSITE',
                        },
                    }));
                    if (window && window.newrelic) {
                        if (isV2Enabled) {
                            window.newrelic.addPageAction('ThirdPartyAction', {
                                id,
                                payloadItems,
                                component: 'EsntialEligibiltyContainer',
                                message: 'Vehicle eligible for Esntial V2',
                            });
                        } else {
                            window.newrelic.addPageAction('ThirdPartyAction', {
                                id,
                                vin,
                                component: 'EsntialEligibiltyContainer',
                                message: 'Vehicle eligible for Esntial',
                            });
                        }
                    }
                } else {
                    // Disable esntial
                    disableEsntial();
                    let message = 'Vehicle ineligible for Esntial';
                    if (response?.circuitBreakerOpen) {
                        message = 'Esntial Eligibility Circuit Breaker Open; displaying as vehicle ineligible for Esntial';
                    }
                    // eslint-disable-next-line no-console
                    console.log(message, vin);
                    if (window && window.newrelic) {
                        if (isV2Enabled) {
                            window.newrelic.addPageAction('ThirdPartyError', {
                                id,
                                payloadItems,
                                component: 'EsntialEligibiltyContainer',
                                message: message + ' V2',
                            });
                        } else {
                            window.newrelic.addPageAction('ThirdPartyError', {
                                id,
                                vin,
                                component: 'EsntialEligibiltyContainer',
                                message,
                            });
                        }
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin, existingDeal.isEcommerce]);

    return null;
}

export default EsntialEligibilityContainer;
